import React, { useEffect, useState } from "react";
import {
  createTimetableDay,
  getAllTimetableDays,
  deleteTimetableDay,
} from "../services/adminTimeTableServices";
import TimetableDayAccordion from "../components/time_table/TimetableDayAccordion";
import { utcToLocalDate } from "../../utils/dateTimeUtils";

const adminTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

const AdminTimetablePage = () => {
  const [days, setDays] = useState([]);
  const [newDate, setNewDate] = useState("");
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    loadTimetable();
  }, []);

  const loadTimetable = async () => {
    try {
      setLoading(true);

      //console.log("⏳ Fetching timetable from API...");
      const res = await getAllTimetableDays();
      //console.log("📦 RAW API RESPONSE:", res.data);

      const convertedDays = res.data.map((day) => {
        // Log each UTC date coming from backend
        //console.log("🔹 Backend UTC date:", day.date);

        const localDate = utcToLocalDate(day.date, adminTimeZone);
        //console.log("🔹 Converted to Admin Local Date:", localDate);

        // Sort sessions and keep UTC for reference
        const sessions = day.sessions
          ? [...day.sessions].sort(
              (a, b) => new Date(a.startTime) - new Date(b.startTime)
            )
          : [];

        return {
          ...day,
          localDate,  // display only
          utcDate: day.date,  // for sorting/comparison
          sessions,
        };
      });

      //console.log("✅ Converted timetable days:", convertedDays);

      // Sort by UTC date to keep timeline correct
      convertedDays.sort((a, b) => new Date(a.utcDate) - new Date(b.utcDate));
      //console.log("📊 Sorted timetable days:", convertedDays);

      setDays(convertedDays);
    } catch (err) {
      //console.error("❌ Failed to fetch timetable", err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateDay = async () => {
    if (!newDate) return alert("Please select a date");

    try {
      //console.log("➕ Creating new timetable day:", newDate, adminTimeZone);
      setCreating(true);

      await createTimetableDay({ date: newDate, timeZone: adminTimeZone });
      //console.log("✅ Timetable day created successfully");

      setNewDate("");
      loadTimetable();
    } catch (err) {
      //console.error("❌ Failed to create day", err);
      alert(err.response?.data?.message || "Failed to create day");
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteDay = async (dayId) => {
    if (!window.confirm("Are you sure you want to delete this day?")) return;

    try {
      //console.log("🗑 Deleting timetable day with ID:", dayId);
      await deleteTimetableDay(dayId);
      //console.log("✅ Timetable day deleted successfully");
      loadTimetable();
    } catch (err) {
      //console.error("❌ Failed to delete day", err);
      alert(err.response?.data?.message || "Failed to delete day");
    }
  };

  return (
    <div className="admin-page">
      <h2>📅 Timetable Management</h2>

      {/* Add Day */}
      <div className="add-day">
        <input
          type="date"
          value={newDate}
          onChange={(e) => setNewDate(e.target.value)}
        />
        <button onClick={handleCreateDay} disabled={creating}>
          {creating ? "Adding..." : "➕ Add Day"}
        </button>
      </div>

      {loading ? (
        <p>Loading timetable...</p>
      ) : days.length === 0 ? (
        <p>No timetable days found.</p>
      ) : (
        days.map((day) => {
          //console.log("🎯 Rendering Day in UI:", day);
          return (
            <TimetableDayAccordion
              key={day.id}
              day={day}
              reload={loadTimetable}
              deleteDay={handleDeleteDay}
            />
          );
        })
      )}
    </div>
  );
};

export default AdminTimetablePage;
